import LeadEvent, {LeadEventTypeEnum} from "./LeadEvent";
import * as moment from "moment";
import {LeadAgentChangeEventDataApiDto, LeadEventApiDto} from "../../../api/model/rest";
import {getUser} from "../../../store/view/user";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";

/**
 * Lead Agent changed event.
 */
export default class LeadAgentChangedEvent extends LeadEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} leadId lead id.
	 * @param {LeadEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber lead reference number.
	 * @param {LeadAgentChangedEventData} data lead Agent change event data.
	 */
	constructor(public readonly id: string,
		public readonly leadId: string,
		public readonly type: LeadEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: LeadAgentChangedEventData) {
		super(id, leadId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: LeadEventApiDto): LeadAgentChangedEvent {
		const data = LeadAgentChangedEventData.fromJson(json.data as LeadAgentChangeEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new LeadAgentChangedEvent(json.id, json.leadId, LeadEventTypeEnum.AgentAssigneeChanged, entity,
			moment(json.createdAt), json.referenceNumber, data);
	}

	getIcon() {
		return Icon.User;
	}

	getActivityMessage(): string {
		return `Agent changed from ${this.getPreviousDisplayName()} to ${this.getCurrentDisplayName()}`;
	}

	/**
	 * @return {string} display name of previous Agent.
	 */
	getPreviousDisplayName(): string {
		return this.data.previousAgentId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}

	/**
	 * @return {string} display name of current Agent.
	 */
	getCurrentDisplayName(): string {
		return this.data.currentAgentId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}
}

/**
 * Data specific for Agent change event.
 */
export class LeadAgentChangedEventData {
	/**
	 * @param {Option<string>} previousAgentId id of the user being previously assigned as Agent.
	 * @param {Option<string>} currentAgentId id of the user being currently assigned as Agent.
	 */
	constructor(public readonly previousAgentId: Option<string>, public readonly currentAgentId: Option<string>) {}

	public static fromJson(json: LeadAgentChangeEventDataApiDto): LeadAgentChangedEventData {
		return new LeadAgentChangedEventData(option(json.previousAgentId), option(json.currentAgentId));
	}
}